import { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { followUser, unfollowUser, patchFollowingIdsCache } from '../api/user';
import GuestProfileModal from './GuestProfileModal';

const GUEST_FOLLOWING_KEY = "guest_following";
const GUEST_FOLLOWING_EVENT = "guestFollowingUpdated";
const GUEST_PROFILE_KEY = "guest_profile";
const USER_FOLLOWING_UPDATED_EVENT = "userFollowingUpdated";

const toIdString = (value) => {
  if (value === null || value === undefined) return '';

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim();
  }

  if (typeof value === 'object') {
    const nestedId = value._id || value.id;
    if (nestedId) return String(nestedId).trim();

    const asString = typeof value.toString === 'function' ? value.toString() : '';
    return asString && asString !== '[object Object]' ? String(asString).trim() : '';
  }

  return '';
};

const readGuestFollowing = () => {
  try {
    const raw = localStorage.getItem(GUEST_FOLLOWING_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveGuestFollowing = (ids) => {
  try {
    localStorage.setItem(GUEST_FOLLOWING_KEY, JSON.stringify(ids));
    window.dispatchEvent(new Event(GUEST_FOLLOWING_EVENT));
  } catch (error) {
    console.error("Error saving guest following:", error);
  }
};

const getGuestProfile = () => {
  try {
    const raw = localStorage.getItem(GUEST_PROFILE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveGuestProfile = (name, photo = "") => {
  try {
    const profile = {
      name: name,
      photo: photo,
      createdAt: new Date().toISOString()
    };
    localStorage.setItem(GUEST_PROFILE_KEY, JSON.stringify(profile));
  } catch (error) {
    console.error("Error saving guest profile:", error);
  }
};

const computeIsFollowing = (currentUser, currentUserId, targetUser, targetUserId) => {
  if (!targetUserId) return false;

  if (currentUser && currentUserId) {
    return Array.isArray(targetUser?.followers)
      && targetUser.followers.some((followerId) => toIdString(followerId) === currentUserId);
  }

  return readGuestFollowing().includes(targetUserId);
};

const notifyFollowingUpdated = (isFollowing, targetUserId, currentUserId = "") => {
  window.dispatchEvent(new CustomEvent(USER_FOLLOWING_UPDATED_EVENT, {
    detail: {
      isFollowing,
      targetUserId,
      currentUserId,
    },
  }));
};

const FollowUnfollowButton = ({ 
  targetUser, 
  onFollowChange, 
  className = "",
  showConfirmation = true,
  size = "medium" // "small", "medium", "large"
}) => {
  const { currentUser, token } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [pendingFollowAction, setPendingFollowAction] = useState(null);
  const currentUserId = toIdString(currentUser?.id || currentUser?._id);
  const targetUserId = toIdString(targetUser?._id || targetUser?.id);
  const [isFollowing, setIsFollowing] = useState(() =>
    computeIsFollowing(currentUser, currentUserId, targetUser, targetUserId)
  );

  // Recompute only when the viewer or target identity changes (avoid stale prop resets).
  useEffect(() => {
    setIsFollowing(computeIsFollowing(currentUser, currentUserId, targetUser, targetUserId));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally identity-only
  }, [currentUserId, targetUserId]);

  useEffect(() => {
    const handleFollowingUpdated = (event) => {
      const eventTargetId = toIdString(event?.detail?.targetUserId);
      if (!eventTargetId || eventTargetId !== targetUserId) return;
      if (typeof event?.detail?.isFollowing === "boolean") {
        setIsFollowing(event.detail.isFollowing);
      }
    };

    const handleGuestFollowingUpdated = () => {
      if (!currentUserId) {
        setIsFollowing(readGuestFollowing().includes(targetUserId));
      }
    };

    window.addEventListener(USER_FOLLOWING_UPDATED_EVENT, handleFollowingUpdated);
    window.addEventListener(GUEST_FOLLOWING_EVENT, handleGuestFollowingUpdated);

    return () => {
      window.removeEventListener(USER_FOLLOWING_UPDATED_EVENT, handleFollowingUpdated);
      window.removeEventListener(GUEST_FOLLOWING_EVENT, handleGuestFollowingUpdated);
    };
  }, [currentUserId, targetUserId]);

  // Don't show button if no target user or viewing own profile
  if (!targetUser || !targetUserId || (currentUserId && currentUserId === targetUserId)) {
    return null;
  }

  const handleGuestProfileSave = (guestName, guestPhoto) => {
    saveGuestProfile(guestName, guestPhoto);
    setShowGuestModal(false);
    
    // Execute the pending follow action
    if (pendingFollowAction) {
      executeGuestFollow(pendingFollowAction);
      setPendingFollowAction(null);
    }
  };

  const executeGuestFollow = (targetId) => {
    const guestFollowing = readGuestFollowing();
    const updatedFollowing = Array.from(new Set([...guestFollowing, targetId]));
    saveGuestFollowing(updatedFollowing);
    setIsFollowing(true);
    
    setNotification("You're now following this user. Login to save permanently.");
    setTimeout(() => setNotification(null), 4000);

    notifyFollowingUpdated(true, targetId);
    if (onFollowChange) {
      onFollowChange(true, targetId);
    }
  };

  const handleFollowToggle = async () => {
    if (isProcessing) return;

    if (isFollowing && showConfirmation) {
      const confirmed = window.confirm('Are you sure you want to unfollow this user?');
      if (!confirmed) return;
    }

    setIsProcessing(true);
    const nextFollowing = !isFollowing;

    try {
      if (currentUser && token && currentUserId) {
        // Logged-in user: use API
        if (isFollowing) {
          await unfollowUser(targetUserId, token, currentUserId);
          setNotification('Unfollowed successfully.');
        } else {
          await followUser(targetUserId, token, currentUserId);
          setNotification("You're now following this user.");
        }

        patchFollowingIdsCache(currentUserId, targetUserId, nextFollowing);
        setIsFollowing(nextFollowing);

        notifyFollowingUpdated(nextFollowing, targetUserId, currentUserId);

        if (onFollowChange) {
          onFollowChange(nextFollowing, targetUserId);
        }
      } else {
        // Guest user: check if profile exists
        if (isFollowing) {
          // Unfollow
          const guestFollowing = readGuestFollowing();
          const updatedFollowing = guestFollowing.filter(id => id !== targetUserId);
          saveGuestFollowing(updatedFollowing);
          setIsFollowing(false);
          setNotification('Unfollowed successfully.');

          notifyFollowingUpdated(false, targetUserId);
          if (onFollowChange) {
            onFollowChange(false, targetUserId);
          }
        } else {
          // Follow - check if guest profile exists
          const guestProfile = getGuestProfile();
          if (!guestProfile) {
            // Show modal to get guest name
            setPendingFollowAction(targetUserId);
            setShowGuestModal(true);
            setIsProcessing(false);
            return;
          } else {
            // Profile exists, proceed with follow
            executeGuestFollow(targetUserId);
          }
        }
      }

      setTimeout(() => setNotification(null), 4000);
    } catch (err) {
      console.error('Error following/unfollowing user:', err);
      setNotification('An error occurred. Please try again.');
      setTimeout(() => setNotification(null), 3000);
    } finally {
      setIsProcessing(false);
    }
  };

  // Size classes
  const sizeClasses = {
    small: 'px-3 py-1 text-sm',
    medium: 'px-4 py-2 text-base',
    large: 'px-6 py-3 text-lg'
  };

  const buttonClasses = `
    ${sizeClasses[size]}
    rounded-full font-medium transition-all duration-200
    ${isFollowing
      ? 'bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600'
      : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg'
    }
    ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
    ${className}
  `.trim();

  return (
    <>
      <button
        onClick={handleFollowToggle}
        disabled={isProcessing}
        className={buttonClasses}
        aria-label={isFollowing ? 'Unfollow user' : 'Follow user'}
      >
        {isProcessing
          ? (isFollowing ? 'Unfollowing...' : 'Following...')
          : (isFollowing ? 'Following' : 'Follow')
        }
      </button>

      {/* Guest Profile Modal */}
      <GuestProfileModal
        isOpen={showGuestModal}
        onClose={() => {
          setShowGuestModal(false);
          setPendingFollowAction(null);
        }}
        onSave={handleGuestProfileSave}
      />

      {/* Notification */}
      {notification && (
        <div className="fixed top-4 right-4 z-50 px-4 py-2 rounded-md bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 shadow-lg flex items-center gap-2 animate-fade-in">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414 1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span className="text-sm">{notification}</span>
        </div>
      )}
    </>
  );
};

export default FollowUnfollowButton;
